import axios from 'axios'
import { Message, MessageBox, Loading } from 'element-ui'
import { getToken } from '@/utils/auth'
import store from '@/store'

let loadingInstance;
let loadingCount = 0;

function startLoading() {
    if (loadingCount === 0) {
        loadingInstance = Loading.service({
            lock: true,
            text: '拼命加载中...',
            spinner: 'el-icon-loading',
            background: 'rgba(255, 255, 255, 0.7)'
        });
    }
    loadingCount++;
}

function stopLoading() {
    loadingCount--;
    if (loadingCount <= 0) {
        loadingCount = 0;
        loadingInstance && loadingInstance.close();
    }
}

// 创建 axios 实例
const service = axios.create({
    // axios 中请求配置有 baseURL 选项，表示请求 URL 公共部分
    baseURL: process.env.VUE_APP_BASE_API,
    // 超时时间（毫秒）
    timeout: 100000
})

// 请求拦截器
service.interceptors.request.use(
    config => {
        // 是否需要设置 token（某些接口可通过 headers.isToken = false 禁用）
        const isToken = (config.headers || {}).isToken === false
        if (getToken() && !isToken) {
            config.headers['Authorization'] = 'Bearer ' + getToken()
        }

        // 默认显示加载动画，除非配置了 loading: false
        if (config.loading !== false) {
            startLoading();
        }

        // 如果是 GET 请求，我们将 params 序列化到 URL 中（支持数组传参）
        if (config.method === 'get' && config.params) {
            let url = config.url + (config.url.indexOf('?') === -1 ? '?' : '&')
            for (const propName of Object.keys(config.params)) {
                const value = config.params[propName]
                if (value !== void 0 && value !== null && value !== '') {
                    if (Array.isArray(value)) {
                        for (const v of value) {
                            url += `${propName}=${encodeURIComponent(v)}&`
                        }
                    } else if (typeof value === 'object') {
                        // 处理 Ruoyi 框架约定的日期范围子对象: { beginXxx, endXxx }
                        // 序列化为 propName%5BsubKey%5D=value，即 params%5BbeginCreateTime%5D=2026-04-01
                        for (const subKey of Object.keys(value)) {
                            const subVal = value[subKey]
                            if (subVal !== void 0 && subVal !== null && subVal !== '') {
                                url += `${propName}%5B${subKey}%5D=${encodeURIComponent(subVal)}&`
                            }
                        }
                    } else {
                        url += `${propName}=${encodeURIComponent(value)}&`
                    }
                }
            }
            if (url.endsWith('&') || url.endsWith('?')) {
                url = url.slice(0, -1)
            }
            config.params = {}
            config.url = url
        }
        return config
    },
    error => {
        console.error('请求错误:', error)
        if (error.config && error.config.loading !== false) {
            stopLoading();
        }
        return Promise.reject(error)
    }
)

// 响应拦截器
service.interceptors.response.use(
    res => {
        if (res.config && res.config.loading !== false) {
            stopLoading();
        }
        // 未设置状态码则默认成功状态
        const code = res.data.code || 200
        // 获取错误信息
        const msg = res.data.msg || '未知错误'

        // 二进制数据则直接返回
        if (res.request.responseType === 'blob' || res.request.responseType === 'arraybuffer') {
            return res.data
        }

        if (code === 401) {
            // 登录状态已过期，清空仓库 token 及状态
            store.commit('LOGOUT')
            // 登录状态已过期，可在此处理登录跳转
            MessageBox.confirm('登录状态已过期，请重新登录', '系统提示', {
                confirmButtonText: '重新登录',
                cancelButtonText: '取消',
                type: 'warning',
                confirmButtonClass: 'el-button--danger'
            }).then(() => {
                // 可在此处理登出逻辑
                location.href = '/login'
            }).catch(() => { })
            return Promise.reject(new Error('登录状态已过期'))
        } else if (code === 500) {
            Message({ message: msg, type: 'error' })
            return Promise.reject(new Error(msg))
        } else if (code !== 200) {
            Message({ message: msg, type: 'error' })
            return Promise.reject(new Error(msg))
        } else {
            return res.data
        }
    },
    error => {
        console.error('响应错误:', error)
        let { message } = error
        if (message === 'Network Error') {
            message = '网络连接异常，请检查网络'
        } else if (message.includes('timeout')) {
            message = '请求超时，请稍后重试'
        } else if (message.includes('Request failed with status code')) {
            message = '服务异常：' + message.substr(message.length - 3)
        }
        Message({ message: message, type: 'error', duration: 5 * 1000 })
        if (error.config && error.config.loading !== false) {
            stopLoading();
        }
        return Promise.reject(error)
    }
)

export default service
